import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArticleList, ArticleRow } from "../components/article-row";
import { useInitDraft } from "../hooks/use-init-draft";
import { useReturnReasons } from "../hooks/use-return-reasons";
import { useReturnableItems } from "../hooks/use-returnable-items";
import { useSetAddress } from "../hooks/use-set-address";
import { useWidgetParams } from "../hooks/use-widget-params";
import { usePublicOrder } from "../queries/use-public-order";
import { useReturnRequests } from "../queries/use-return-requests";
import { draftReturnAtomFamily } from "../atoms/draft-return.atom";
import { createDraft } from "../lib/draft.lib";
import { useSetAtom } from "jotai";
import { useCustomText } from "../hooks/use-custom-text";

export function RetractationArticleSelectionPage() {
  const { orderId, shopId, shopInternalId, shopSlug, logoUrl } = useWidgetParams();
  const navigate = useNavigate();
  useInitDraft(orderId, shopInternalId);

  const returnableItems = useReturnableItems(orderId, shopSlug);
  const { data: returnRequestsData } = useReturnRequests({ orderId, shopSlug });
  const { data: order } = usePublicOrder(orderId);
  const reasons = useReturnReasons(shopId);
  const setAddress = useSetAddress(orderId);
  const setDraft = useSetAtom(draftReturnAtomFamily(orderId));

  const returnedLineItemIds = new Set<string>(
    returnRequestsData?.returnRequests?.flatMap(
      (rr) => rr.line_items?.map((li) => li.line_item_id) ?? [],
    ) ?? [],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const t = useCustomText();

  const toggle = (lineItemId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(lineItemId) ? next.delete(lineItemId) : next.add(lineItemId);
      return next;
    });
  };

  const handleSubmit = () => {
    const firstReason = reasons[0] ?? null;

    const draft = createDraft(orderId, shopInternalId);
    draft.lineItems = Array.from(selected).map((lineItemId) => ({
      lineItemId,
      quantity: 1,
      reasonId: firstReason?.id ?? null,
      comment: null,
      imageUrls: [],
      resolution: "refund",
      resolutionOption: null,
      exchangeVariantId: null,
    }));

    const orderAddress = order?.shippingAddress ?? null;
    if (orderAddress) {
      draft.address = orderAddress;
      setAddress(orderAddress);
    }

    draft.returnMode = "retractation";
    setDraft(draft);

    navigate(`/return-widget/${shopId}/order/${orderId}/retractation-summary`);
  };

  if (returnableItems.length === 0 && returnedLineItemIds.size === 0) {
    return (
      <WidgetPage>
        <Header>
          <ShopLogo src={logoUrl ?? undefined} />
          <HeaderTitle>{t("articleSelection.title")}</HeaderTitle>
        </Header>
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
          <p className="font-sans text-sm font-medium">{t("articleSelection.empty.title")}</p>
          <p className="font-content text-sm text-muted-foreground">
            {t("articleSelection.empty.description")}
          </p>
        </div>
      </WidgetPage>
    );
  }

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("articleSelection.title")}</HeaderTitle>
        <HeaderDescription>
          {t("articleSelection.description")}
        </HeaderDescription>
      </Header>
      <ArticleList className="overflow-y-auto">
          {returnableItems.map((item) => (
            <ArticleRow
              key={item.line_item_id}
              item={item}
              checked={selected.has(item.line_item_id)}
              onToggle={() => toggle(item.line_item_id)}
            />
          ))}
          {Array.from(returnedLineItemIds).map((lineItemId) => {
            const item = returnableItems.find((i) => i.line_item_id === lineItemId);
            if (!item) return null;
            return (
              <ArticleRow
                key={`returned-${lineItemId}`}
                item={item}
                checked={false}
                onToggle={() => {}}
                disabled
                badge={t("retractation.returnedBadge")}
              />
            );
          })}
      </ArticleList>
      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
          <Button
            className="w-full"
            size="lg"
            onClick={handleSubmit}
            disabled={selected.size === 0}
          >
            {t("common.continue")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
